import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { getUserProfile, saveUserProfile, uploadUserDocument, deleteUserDocument } from '../api/users';
import { autocompleteAddress, getPlaceDetails } from '../api/address';
import { Avatar } from '../components/Avatar';
import { LoadingAnimation } from '../components/LoadingAnimation';
import { API_URL } from '../config/api';

// Lazy loading of Mapbox components
const Map = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Map })));
const Marker = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Marker })));
const Source = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Source })));
const Layer = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.Layer })));
const NavigationControl = lazy(() => import('react-map-gl/mapbox').then(module => ({ default: module.NavigationControl })));

// User Location Map Component
interface UserLocationMapProps {
  address: {
    latitude: number;
    longitude: number;
    formatted_address: string;
  };
  workRange: number;
}

const UserLocationMap: React.FC<UserLocationMapProps> = ({ address, workRange }) => {
  const viewState = {
    latitude: address.latitude,
    longitude: address.longitude,
    zoom: 10,
    maxZoom: 18,
    minZoom: 2,
    bearing: 0,
    pitch: 0,
  };

  return (
    <Suspense fallback={
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-sm">Loading map...</p>
        </div>
      </div>
    }>
      <Map
        mapLib={import('mapbox-gl')}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_TOKEN}
        initialViewState={viewState}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/light-v11"
      >
        {/* User Location Marker */}
        <Marker
          longitude={address.longitude}
          latitude={address.latitude}
          anchor="bottom"
        >
          <div className="relative">
            <div className="w-8 h-8 bg-blue-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
            </div>
          </div>
        </Marker>

        {/* Work Range Circle */}
        <Source
          id="work-range"
          type="geojson"
          data={{
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [address.longitude, address.latitude]
            },
            properties: {}
          }}
        >
          <Layer
            id="work-range-circle"
            type="circle"
            paint={{
              'circle-radius': workRange * 1609.34, // Convert miles to meters
              'circle-color': 'rgba(59, 130, 246, 0.2)',
              'circle-stroke-color': 'rgba(59, 130, 246, 0.8)',
              'circle-stroke-width': 2
            }}
          />
        </Source>

        <NavigationControl position="top-right" />
      </Map>
    </Suspense>
  );
};

// Predefined skill options
const SKILL_OPTIONS = [
  'LV (Low Voltage)',
  'Rack Installation',
  'Software Configuration',
  'Network Setup',
  'Hardware Installation',
  'Cable Management',
  'Testing & Commissioning',
  'Maintenance',
  'Project Management',
  'Technical Documentation'
];

// Address suggestion interface
interface AddressSuggestion {
  place_id: string;
  description: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

// US States/Regions
const US_REGIONS = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming'
];

const Profile: React.FC = () => {
  const { keycloak } = useKeycloak();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isNewProfile, setIsNewProfile] = useState(false);
  const [formData, setFormData] = useState<UserProfileFormData>({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    rate: 0,
    region: '',
    city: '',
    range_miles: 50,
    skills: [],
    position: '',
    photo_url: '',
    google_address_data: undefined
  });
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [uploadingDocuments, setUploadingDocuments] = useState<{[key: string]: boolean}>({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Address autocomplete state
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState<AddressSuggestion[]>([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const addressSuggestionsRef = useRef<HTMLDivElement>(null);

  // Get current user ID from Keycloak
  const currentUserId = keycloak?.tokenParsed?.sub || '';

  useEffect(() => {
    console.log('Profile component mounted');
    console.log('keycloak authenticated:', keycloak?.authenticated);
    console.log('keycloak token exists:', !!keycloak?.token);
    console.log('currentUserId:', currentUserId);
    
    if (keycloak?.authenticated && keycloak?.token && currentUserId) {
      loadUserProfile();
    } else {
      console.log('Missing requirements for profile load - creating default profile');
      setIsNewProfile(true); // Mark as new profile
      
      // Create minimal default profile if no auth data
      const fallbackProfile: UserProfile = {
        id: currentUserId || 'unknown',
        first_name: keycloak?.tokenParsed?.given_name || '',
        last_name: keycloak?.tokenParsed?.family_name || '',
        phone: '',
        email: keycloak?.tokenParsed?.email || '',
        rate: 0,
        region: '',
        city: '',
        range_miles: 50,
        skills: [],
        position: '',
        google_address_data: undefined,
        projects: [],
        managers: [],
      };
      
      setProfile(fallbackProfile);
      setFormData({
        first_name: fallbackProfile.first_name,
        last_name: fallbackProfile.last_name,
        phone: fallbackProfile.phone,
        email: fallbackProfile.email,
        rate: fallbackProfile.rate,
        region: fallbackProfile.region,
        city: fallbackProfile.city,
        range_miles: fallbackProfile.range_miles,
        skills: fallbackProfile.skills,
        position: fallbackProfile.position,
        google_address_data: fallbackProfile.google_address_data
      });
      setSelectedSkills(fallbackProfile.skills || []);
      
      setIsLoading(false);
    }
  }, [keycloak?.token, currentUserId]);

  // Address autocomplete with debouncing
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (addressQuery.length >= 3 && keycloak?.token) {
        setIsLoadingAddress(true);
        try {
          const result = await autocompleteAddress(keycloak.token, addressQuery);
          setAddressSuggestions(result.predictions || []);
          setShowAddressSuggestions(true);
        } catch (error) {
          console.error('Error fetching address suggestions:', error);
          setAddressSuggestions([]);
        } finally {
          setIsLoadingAddress(false);
        }
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [addressQuery, keycloak?.token]);

  // Handle click outside address suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addressSuggestionsRef.current && !addressSuggestionsRef.current.contains(event.target as Node) &&
          addressInputRef.current && !addressInputRef.current.contains(event.target as Node)) {
        setShowAddressSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      const profileData = await getUserProfile(keycloak?.token || '', currentUserId);
      setProfile(profileData);
      
      // Set form data
      setFormData({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        phone: profileData.phone,
        email: profileData.email,
        rate: profileData.rate,
        region: profileData.region,
        city: profileData.city,
        range_miles: profileData.range_miles,
        skills: profileData.skills || [],
        position: profileData.position || '',
        photo_url: profileData.photo_url || '',
        google_address_data: profileData.google_address_data
      });
      setSelectedSkills(profileData.skills || []);
      setAddressQuery(profileData.google_address_data?.formatted_address || '');
    } catch (error) {
      console.error('Failed to load user profile:', error);
      
      // Handle 422 errors gracefully - user profile doesn't exist yet
      if (error instanceof Error && error.message.includes('422')) {
        console.log('👤 User profile not found (422) - this is normal for new users');
        setIsNewProfile(true); // Mark as new profile for save logic
        
        // Create a default profile object for new users
        const defaultProfile: UserProfile = {
          id: keycloak?.tokenParsed?.sub || '',
          first_name: keycloak?.tokenParsed?.given_name || '',
          last_name: keycloak?.tokenParsed?.family_name || '',
          phone: '',
          email: keycloak?.tokenParsed?.email || '',
          rate: 0,
          region: '',
          city: '',
          range_miles: 50,
          skills: [],
          position: '',
          photo_url: '',
          google_address_data: undefined,
          projects: [],
          managers: []
        };
        
        setProfile(defaultProfile);
        setFormData({
          first_name: defaultProfile.first_name,
          last_name: defaultProfile.last_name,
          phone: defaultProfile.phone,
          email: defaultProfile.email,
          rate: defaultProfile.rate,
          region: defaultProfile.region,
          city: defaultProfile.city,
          range_miles: defaultProfile.range_miles,
          skills: defaultProfile.skills,
          position: defaultProfile.position,
          photo_url: defaultProfile.photo_url || '',
          google_address_data: defaultProfile.google_address_data
        });
        setSelectedSkills(defaultProfile.skills || []);
        
        toast.info('Please complete your profile setup');
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'rate' || name === 'range_miles' ? Number(value) : value
    }));
  };

  const handleSkillToggle = (skill: string) => {
    setSelectedSkills(prev => {
      const newSkills = prev.includes(skill) 
        ? prev.filter(s => s !== skill)
        : [...prev, skill];
      
      setFormData(current => ({
        ...current,
        skills: newSkills
      }));
      
      return newSkills;
    });
  };

  // Handle address selection
  const handleAddressSelect = async (suggestion: AddressSuggestion) => {
    if (!keycloak?.token) return;

    setIsLoadingAddress(true);
    try {
      const placeDetails = await getPlaceDetails(keycloak.token, suggestion.place_id);
      setFormData(prev => ({
        ...prev,
        google_address_data: placeDetails
      }));
      setAddressQuery(placeDetails.formatted_address);
      setShowAddressSuggestions(false);
    } catch (error) {
      console.error('Error fetching place details:', error);
      toast.error('Failed to load address details');
    } finally {
      setIsLoadingAddress(false);
    }
  };

  // Clear selected address
  const clearAddress = () => {
    setFormData(prev => ({ 
      ...prev, 
      google_address_data: undefined 
    }));
    setAddressQuery('');
    setShowAddressSuggestions(false);
  };

  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      console.log('💾 Saving profile...', { isNewProfile, userId: currentUserId });
      
      const updatedProfile = await saveUserProfile(
        keycloak?.token || '', 
        currentUserId, 
        formData, 
        isNewProfile
      );
      
      console.log('✅ Profile saved successfully:', updatedProfile);
      setProfile(updatedProfile);
      setIsEditing(false);
      setIsNewProfile(false); // No longer new after successful save
      toast.success(isNewProfile ? 'Profile created successfully!' : 'Profile updated successfully');
    } catch (error) {
      console.error('❌ Failed to save profile:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      // Provide specific error messages
      if (errorMessage.includes('422')) {
        toast.error('Invalid profile data. Please check all required fields.');
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        toast.error('You do not have permission to update this profile.');
      } else if (errorMessage.includes('500')) {
        toast.error('Server error occurred. Please try again in a few moments.');
      } else {
        toast.error(`Failed to save profile: ${errorMessage}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (profile) {
      setFormData({
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        email: profile.email,
        rate: profile.rate,
        region: profile.region,
        city: profile.city,
        range_miles: profile.range_miles,
        skills: profile.skills || [],
        position: profile.position || '',
        photo_url: profile.photo_url || '',
        google_address_data: profile.google_address_data
      });
      setSelectedSkills(profile.skills || []);
      setAddressQuery(profile.google_address_data?.formatted_address || '');
    }
    setIsEditing(false);
  };

  const handleDocumentUpload = async (documentType: string, file: File) => {
    try {
      setUploadingDocuments(prev => ({ ...prev, [documentType]: true }));
      await uploadUserDocument(keycloak?.token || '', currentUserId, documentType, file);
      toast.success(`${documentType.replace('_', ' ')} uploaded successfully`);
      loadUserProfile(); // Refresh profile to get updated document URLs
    } catch (error) {
      console.error(`Failed to upload ${documentType}:`, error);
      toast.error(`Failed to upload ${documentType.replace('_', ' ')}`);
    } finally {
      setUploadingDocuments(prev => ({ ...prev, [documentType]: false }));
    }
  };

  const handleDocumentDelete = async (documentType: string) => {
    if (window.confirm(`Are you sure you want to delete this ${documentType.replace('_', ' ')}?`)) {
      try {
        await deleteUserDocument(keycloak?.token || '', currentUserId, documentType);
        toast.success(`${documentType.replace('_', ' ')} deleted successfully`);
        loadUserProfile(); // Refresh profile to clear document URLs
      } catch (error) {
        console.error(`Failed to delete ${documentType}:`, error);
        toast.error(`Failed to delete ${documentType.replace('_', ' ')}`);
      }
    }
  };

  const handlePhotoUpload = async (file: File) => {
    try {
      setUploadingPhoto(true);
      
      // Create FormData for file upload
      const fileFormData = new FormData();
      fileFormData.append('file', file);
      
      // Upload photo to get URL
      const response = await fetch(`${API_URL}/users/${currentUserId}/profile/photo`, {
        method: 'POST',
        headers: {
          'authorization': 'Bearer ' + keycloak?.token
        },
        body: fileFormData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      const photoUrl = result.photo_url || result.url;
      
      if (photoUrl) {
        // Update form data with new photo URL
        setFormData(prev => ({ ...prev, photo_url: photoUrl }));
        toast.success('Photo uploaded successfully. Save profile to confirm changes.');
      } else {
        throw new Error('No photo URL returned from server');
      }
      
    } catch (error) {
      console.error('Failed to upload photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDocumentView = async (documentUrl?: string) => {
    if (!documentUrl || !keycloak?.token) {
      toast.error('Document not available');
      return;
    }

    try {
      console.log('Downloading document with token:', documentUrl);
      
      const response = await fetch(documentUrl, {
        method: 'GET',
        headers: {
          'authorization': 'Bearer ' + keycloak.token
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      const openableTypes = ['pdf', 'image', 'text', 'video', 'audio'];
      const contentType = response.headers.get('content-type') || '';
      const canOpen = openableTypes.some(type => 
        contentType.toLowerCase().includes(type)
      );

      if (canOpen) {
        window.open(url, '_blank');
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 1000);
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.download = 'document';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to view document:', error);
      toast.error('Failed to view document');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingAnimation />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Profile not found</p>
          <button 
            onClick={loadUserProfile}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Avatar
                firstName={profile.first_name}
                lastName={profile.last_name}
                email={profile.email}
                size={80}
                src={formData.photo_url || profile.photo_url}
              />
              {isEditing && (
                <div className="absolute -bottom-2 -right-2">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handlePhotoUpload(file);
                    }}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="flex items-center justify-center w-8 h-8 bg-blue-500 text-white rounded-full cursor-pointer hover:bg-blue-600 shadow-lg"
                  >
                    {uploadingPhoto ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </label>
                </div>
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {profile.first_name} {profile.last_name}
              </h1>
              <p className="text-gray-600">{profile.email}</p>
              {profile.position && (
                <p className="text-gray-600">{profile.position}</p>
              )}
              <p className="text-gray-500">{profile.city}, {profile.region}</p>
            </div>
          </div>
          <div className="flex space-x-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
              >
                Edit Profile
              </button>
            ) : (
              <>
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveProfile}
                  disabled={isSaving}
                  className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Personal Information */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Personal Information</h2>
          
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Position
                </label>
                <input
                  type="text"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  placeholder="e.g., Senior Frontend Developer"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <div className="relative">
                  <input
                    ref={addressInputRef}
                    type="text"
                    value={addressQuery}
                    onChange={(e) => setAddressQuery(e.target.value)}
                    placeholder="Start typing an address..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {isLoadingAddress && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                  {formData.google_address_data && (
                    <button
                      type="button"
                      onClick={clearAddress}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Address Suggestions */}
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div
                    ref={addressSuggestionsRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                  >
                    {addressSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        type="button"
                        onClick={() => handleAddressSelect(suggestion)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        <div className="font-medium text-gray-900">
                          {suggestion.structured_formatting.main_text}
                        </div>
                        <div className="text-sm text-gray-500">
                          {suggestion.structured_formatting.secondary_text}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <span className="text-sm font-medium text-gray-500">Name:</span>
                <p className="text-gray-900">{profile.first_name} {profile.last_name}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Email:</span>
                <p className="text-gray-900">{profile.email}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Phone:</span>
                <p className="text-gray-900">{profile.phone || 'Not provided'}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-500">Position:</span>
                <p className="text-gray-900">{profile.position || 'Not specified'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Location & Map */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Location & Work Range</h2>
          
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Region/State
                  </label>
                  <select
                    name="region"
                    value={formData.region}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Region</option>
                    {US_REGIONS.map(region => (
                      <option key={region} value={region}>{region}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <div className="relative">
                  <input
                    ref={addressInputRef}
                    type="text"
                    value={addressQuery}
                    onChange={(e) => setAddressQuery(e.target.value)}
                    placeholder="Start typing an address..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {isLoadingAddress && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                  {formData.google_address_data && (
                    <button
                      type="button"
                      onClick={clearAddress}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
                
                {/* Address Suggestions */}
                {showAddressSuggestions && addressSuggestions.length > 0 && (
                  <div
                    ref={addressSuggestionsRef}
                    className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto"
                  >
                    {addressSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        type="button"
                        onClick={() => handleAddressSelect(suggestion)}
                        className="w-full px-4 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        <div className="font-medium text-gray-900">
                          {suggestion.structured_formatting.main_text}
                        </div>
                        <div className="text-sm text-gray-500">
                          {suggestion.structured_formatting.secondary_text}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hourly Rate ($)
                  </label>
                  <input
                    type="number"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work Range (miles)
                  </label>
                  <input
                    type="number"
                    name="range_miles"
                    value={formData.range_miles}
                    onChange={handleInputChange}
                    min="1"
                    max="500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Address:</span>
                  <p className="text-gray-900">{profile.google_address_data?.formatted_address || 'Not specified'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Location:</span>
                  <p className="text-gray-900">{profile.city}, {profile.region}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Hourly Rate:</span>
                  <p className="text-gray-900">${profile.rate}/hour</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Work Range:</span>
                  <p className="text-gray-900">{profile.range_miles} miles</p>
                </div>
              </div>
              
              {/* Map Section */}
              {profile.google_address_data && (
                <div className="mt-4">
                  <h3 className="text-md font-medium mb-2">Work Area Map</h3>
                  <div className="h-64 rounded-lg overflow-hidden border border-gray-200">
                    <UserLocationMap 
                      address={profile.google_address_data}
                      workRange={profile.range_miles}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Skills */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Skills & Expertise</h2>
          
          {isEditing ? (
            <div className="grid grid-cols-2 gap-2">
              {SKILL_OPTIONS.map(skill => (
                <label key={skill} className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedSkills.includes(skill)}
                    onChange={() => handleSkillToggle(skill)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{skill}</span>
                </label>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {profile.skills && profile.skills.length > 0 ? (
                profile.skills.map(skill => (
                  <span
                    key={skill}
                    className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {skill}
                  </span>
                ))
              ) : (
                <p className="text-gray-500">No skills added yet</p>
              )}
            </div>
          )}
        </div>

        {/* Documents */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Documents</h2>
          
          <div className="space-y-4">
            {/* NDA Document */}
            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
              <div>
                <p className="font-medium">NDA Document</p>
                <p className="text-sm text-gray-500">
                  {profile.nda_document ? 'Uploaded' : 'Not uploaded'}
                </p>
              </div>
              <div className="flex space-x-2">
                {profile.nda_document && (
                  <button
                    onClick={() => handleDocumentView(profile.nda_document)}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    View
                  </button>
                )}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleDocumentUpload('NDA', file);
                  }}
                  className="hidden"
                  id="nda-upload"
                />
                <label
                  htmlFor="nda-upload"
                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 cursor-pointer"
                >
                  {uploadingDocuments.NDA ? 'Uploading...' : 'Upload'}
                </label>
                {profile.nda_document && (
                  <button
                    onClick={() => handleDocumentDelete('NDA')}
                    className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Contract Document */}
            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
              <div>
                <p className="font-medium">Contract Document</p>
                <p className="text-sm text-gray-500">
                  {profile.contract_document ? 'Uploaded' : 'Not uploaded'}
                </p>
              </div>
              <div className="flex space-x-2">
                {profile.contract_document && (
                  <button
                    onClick={() => handleDocumentView(profile.contract_document)}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    View
                  </button>
                )}
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleDocumentUpload('CONTRACT', file);
                  }}
                  className="hidden"
                  id="contract-upload"
                />
                <label
                  htmlFor="contract-upload"
                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 cursor-pointer"
                >
                  {uploadingDocuments.CONTRACT ? 'Uploading...' : 'Upload'}
                </label>
                {profile.contract_document && (
                  <button
                    onClick={() => handleDocumentDelete('CONTRACT')}
                    className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>

            {/* Driver License */}
            <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md">
              <div>
                <p className="font-medium">Driver's License</p>
                <p className="text-sm text-gray-500">
                  {profile.driver_license_document ? 'Uploaded' : 'Not uploaded'}
                </p>
              </div>
              <div className="flex space-x-2">
                {profile.driver_license_document && (
                  <button
                    onClick={() => handleDocumentView(profile.driver_license_document)}
                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
                  >
                    View
                  </button>
                )}
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleDocumentUpload('DRIVER_LICENSE', file);
                  }}
                  className="hidden"
                  id="license-upload"
                />
                <label
                  htmlFor="license-upload"
                  className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 cursor-pointer"
                >
                  {uploadingDocuments.DRIVER_LICENSE ? 'Uploading...' : 'Upload'}
                </label>
                {profile.driver_license_document && (
                  <button
                    onClick={() => handleDocumentDelete('DRIVER_LICENSE')}
                    className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Projects */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Projects</h2>
          
          {profile.projects && profile.projects.length > 0 ? (
            <div className="space-y-2">
              {profile.projects.map((project, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded-md">
                  <p className="font-medium">{project.name || `Project ${index + 1}`}</p>
                  <p className="text-sm text-gray-500">{project.description || 'No description'}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No projects assigned yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile; 